import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Car, ArrowLeft, BatteryCharging, Gauge, Plug, Navigation2 } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SeoFooter } from "@/components/SeoFooter";
import { useSeo, useJsonLd, ogImageUrl } from "@/lib/seo";
import { findModel, MODELS, MODEL_PATH } from "@/lib/models";
import { THEMATIC_PAGES } from "@/lib/thematic";

export default function ModelPage() {
  const { slug } = useParams();
  const model = findModel(slug);

  useSeo(
    model ? model.metaTitle : "vspo",
    model ? model.metaDescription : "",
    model ? `${MODEL_PATH}/${model.slug}` : undefined,
    model ? ogImageUrl({ title: model.name, subtitle: "Voiture sans permis" }) : undefined
  );

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  useJsonLd(
    "model-jsonld",
    model
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Accueil", item: `${origin}/` },
              { "@type": "ListItem", position: 2, name: model.name, item: `${origin}${MODEL_PATH}/${model.slug}` },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: (model.faq || []).map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]
      : null
  );

  if (!model) return <Navigate to="/" replace />;

  const others = MODELS.filter((m) => m.slug !== model.slug);

  return (
    <div className="min-h-screen w-full overflow-y-auto bg-background">
      <header className="sticky top-0 z-10 border-b border-border bg-card/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Car className="h-5 w-5" />
            </span>
            <div className="leading-tight">
              <div className="font-display text-lg font-bold text-foreground">vspo</div>
              <div className="text-[11px] text-muted-foreground">GPS voiture sans permis · 45 km/h</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground"
              data-testid="model-open-app"
            >
              <ArrowLeft className="h-4 w-4" /> Ouvrir le GPS
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 pb-16 pt-6">
        <nav className="mb-3 text-xs text-muted-foreground" aria-label="Fil d'ariane">
          <Link to="/" className="hover:text-primary">Accueil</Link> ·{" "}
          <span className="text-foreground">{model.name}</span>
        </nav>

        <div className="mb-2 flex items-center gap-2">
          <BatteryCharging className="h-5 w-5 text-primary" />
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {model.energy}
          </span>
        </div>
        <h1 className="font-display text-3xl font-bold text-foreground" data-testid="model-title">
          {model.h1}
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">{model.intro}</p>

        <section className="mt-6 grid gap-2 rounded-2xl border border-border bg-card p-4 sm:grid-cols-2">
          {model.specs.map((s) => (
            <div key={s.label} className="flex items-start gap-2">
              <span className="mt-0.5 text-primary">
                {s.label.toLowerCase().includes("vitesse") ? (
                  <Gauge className="h-4 w-4" />
                ) : s.label.toLowerCase().includes("prise") ? (
                  <Plug className="h-4 w-4" />
                ) : (
                  <BatteryCharging className="h-4 w-4" />
                )}
              </span>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {s.label}
                </div>
                <div className="text-sm text-foreground">{s.value}</div>
              </div>
            </div>
          ))}
        </section>

        {model.body.map((sec) => (
          <section className="mt-8" key={sec.h2}>
            <h2 className="font-display text-xl font-bold text-foreground">{sec.h2}</h2>
            <div className="mt-2 space-y-3 text-[15px] leading-relaxed text-muted-foreground">
              {sec.paragraphs.map((p) => (
                <p key={p.slice(0, 40)}>{p}</p>
              ))}
            </div>
          </section>
        ))}

        <div className="mt-10 rounded-2xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-bold text-foreground">
            Calculez un trajet pour votre {model.name}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Itinéraires 45 km/h sans autoroute, suivi de batterie et arrêt recharge sur le trajet.
          </p>
          <Link
            to="/"
            className="mt-3 inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground"
            data-testid="model-cta"
          >
            <Navigation2 className="h-4 w-4" /> Ouvrir le GPS
          </Link>
        </div>

        {model.faq && model.faq.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-base font-bold text-foreground">Questions fréquentes</h2>
            <div className="mt-3 space-y-3">
              {model.faq.map((f) => (
                <div key={f.q} className="rounded-xl border border-border bg-card p-4">
                  <h3 className="text-[15px] font-semibold text-foreground">{f.q}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        <section className="mt-10">
          <h2 className="font-display text-base font-bold text-foreground">Autres modèles</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {others.map((m) => (
              <Link
                key={m.slug}
                to={`${MODEL_PATH}/${m.slug}`}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-primary"
                data-testid={`model-link-${m.slug}`}
              >
                {m.name}
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-8">
          <div className="flex flex-wrap gap-2">
            {THEMATIC_PAGES.map((t) => (
              <Link
                key={t.slug}
                to={t.path}
                className="text-sm text-primary hover:underline"
                data-testid={`model-thematic-${t.slug}`}
              >
                {t.kicker}
              </Link>
            ))}
          </div>
        </section>
      </main>

      <SeoFooter />
    </div>
  );
}
